import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useFirebaseAuth } from './FirebaseAuthProvider';

const propTypes = {
  size: PropTypes.string,
};
const defaultProps = {
  size: 'large',
};

const sizeClassMap = {
  extraSmall: 'utah-id-login__logo--extra-small',
  small: 'utah-id-login__logo--small',
  medium: 'utah-id-login__logo--medium',
  large: 'utah-id-login__logo--large',
  extraLarge: 'utah-id-login__logo--extra-large',
};

// Session-scoped flag so we auto-redirect to id.utah.gov at most once per
// browser session. Without this, a user who returns from id.utah.gov still
// unauthenticated (cancelled, not approved, etc.) would be bounced back into an
// infinite redirect loop. The manual button below remains as a fallback.
const AUTO_REDIRECT_KEY = 'uid-pharma:auto-signin-attempted';

/**
 * Public landing / sign-in screen shown to unauthenticated users. On mount it
 * automatically starts the id.utah.gov OIDC sign-in via Firebase
 * (`signInWithRedirect`) once per session; the browser is redirected to
 * id.utah.gov and back, after which FirebaseAuthProvider completes the redirect
 * and loads the session. The button is a manual fallback (e.g. after the
 * one-shot auto-redirect, or when sessionStorage is unavailable).
 */
const UtahIdLogin = ({ size }) => {
  const { login } = useFirebaseAuth() || {};
  const autoTriggered = useRef(false);

  const handlePress = useCallback(async () => {
    if (!login) {
      return;
    }
    try {
      await login();
    } catch {
      /* errors surfaced by FirebaseAuthProvider.completeRedirect */
    }
  }, [login]);

  // Auto-start sign-in once per session.
  useEffect(() => {
    if (!login || autoTriggered.current) {
      return;
    }

    let alreadyAttempted = false;
    try {
      alreadyAttempted =
        window.sessionStorage.getItem(AUTO_REDIRECT_KEY) === 'true';
    } catch {
      /* sessionStorage unavailable — fall back to the manual button */
      return;
    }

    if (alreadyAttempted) {
      return;
    }

    autoTriggered.current = true;
    try {
      window.sessionStorage.setItem(AUTO_REDIRECT_KEY, 'true');
    } catch {
      /* ignore write failures */
    }
    handlePress();
  }, [login, handlePress]);

  const logoSizeClass = sizeClassMap[size] || sizeClassMap.medium;

  return (
    <>
      <div className="home-banner">
        <div className="home-banner__title">UID Drug Cost Transparency</div>
      </div>
      <div className="home-wrapper">
        <div className="content-width home-page__background-section">
          <h2>Welcome to the UID Drug Cost Transparency Website!</h2>
          <div className="utah-id-login">
            <div className="utah-id-login__divider">
              <span className="utah-id-login__divider-line" />
              <span className="utah-id-login__divider-text">continue with</span>
              <span className="utah-id-login__divider-line" />
            </div>
            <div className="utah-id-login__button-container">
              <button
                type="button"
                className="utah-id-login__button"
                onClick={handlePress}
              >
                <span className="visually-hidden">Log in with UtahID</span>
                <span aria-hidden="true" className={logoSizeClass}>
                  Log in with UtahID
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

UtahIdLogin.propTypes = propTypes;
UtahIdLogin.defaultProps = defaultProps;

export default observer(UtahIdLogin);
